import { WEB3FORMS_PUBLIC_KEY, WEB3FORMS_SUBMIT_URL } from '../config/leads';
import { LEAD_ENDPOINT } from '../whitePapers/whitePapers';
import { leadLines, subjectFor } from './leadFormat';

export interface LeadPayload {
  email: string;
  name?: string;
  company?: string;
  /** Honeypot. People leave it empty. */
  website?: string;
  type?: 'contact';
  paper?: string;
  /** Shown in the email only; the server looks the title up itself. */
  paperTitle?: string;
  message?: string;
  heardFrom?: string;
  page?: string;
  referrer?: string;
  utm?: Record<string, string>;
  returning?: boolean;
}

export interface SubmitResult {
  /** HTTP status from /api/lead, or null when it could not be reached. */
  status: number | null;
  error?: string;
  /** True when the browser already delivered the lead through Web3Forms. */
  clientNotified?: boolean;
}

const WEB3FORMS_TIMEOUT_MS = 3000;

/** The JSON body for a browser-side Web3Forms submit. Exported for tests. */
export function web3formsBody(payload: LeadPayload, accessKey: string) {
  const fields = Object.fromEntries(leadLines(payload));
  return {
    access_key: accessKey,
    subject: subjectFor(payload),
    from_name: 'Greyfield Data website',
    botcheck: payload.website || '',
    replyto: payload.email,
    email: payload.email,
    name: payload.name || payload.email,
    ...fields,
  };
}

async function sendToWeb3Forms(payload: LeadPayload): Promise<boolean> {
  const controller = typeof AbortController === 'undefined' ? null : new AbortController();
  const timer = controller ? window.setTimeout(() => controller.abort(), WEB3FORMS_TIMEOUT_MS) : 0;
  try {
    const response = await fetch(WEB3FORMS_SUBMIT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(web3formsBody(payload, WEB3FORMS_PUBLIC_KEY)),
      signal: controller?.signal,
    });
    const data = await response.json().catch(() => ({}));
    return response.ok && data?.success !== false;
  } catch {
    return false;
  } finally {
    if (timer) window.clearTimeout(timer);
  }
}

/**
 * Send a lead. With a Web3Forms key set, the browser emails it through Web3Forms first, then
 * /api/lead keeps a copy in the server log. Without a key, only /api/lead is called, as before.
 */
export async function submitLead(payload: LeadPayload, options: { keepalive?: boolean } = {}): Promise<SubmitResult> {
  const { paperTitle: _title, ...serverPayload } = payload;
  void _title;
  const clientNotified = WEB3FORMS_PUBLIC_KEY ? await sendToWeb3Forms(payload) : false;
  try {
    const response = await fetch(LEAD_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(clientNotified ? { ...serverPayload, clientNotified: true } : serverPayload),
      keepalive: options.keepalive,
    });
    if (response.status === 400) {
      const data = await response.json().catch(() => ({}));
      return { status: 400, error: typeof data.error === 'string' ? data.error : undefined, clientNotified };
    }
    return { status: response.status, clientNotified };
  } catch {
    return { status: null, clientNotified };
  }
}
